/**
 * DiscoveryDialog — modal status panel for schema discovery + profiling.
 *
 * Open ⇒ shows a full-width warning FIRST (Sep 4 2026): a re-discovery
 * rewrites the whole schema and re-runs AI profiling, which throws away
 * every AI-generated description, sample value, semantic mapping and
 * detected relationship. Only what a person marked as edited survives
 * (table/column descriptions with ``user_edited``, and the business
 * context when ``business_context_user_edited``). Nothing fires until the
 * person confirms. Then it fires ``useDiscoverSchema`` once and polls
 * ``useProfilingStatus`` until ``status === "complete"`` (or terminal
 * ``"no_schemas"`` / error). UX mirrors V1 ``DiscoveryDialog.tsx`` +
 * ``useDiscoveryPoller.ts`` but rebuilt against the Ando tokens.
 *
 * Polling auto-stops on unmount (TanStack ``refetchInterval`` returns
 * ``false`` once the query is disabled).
 */

import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { AlertCircle, AlertTriangle, CheckCircle2, CircleDot, Loader2, ShieldCheck } from "lucide-react";
import * as React from "react";

import type { ProfilingStatusResponse } from "@ando/core/api";
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, cn } from "@ando/ui";

import { useDiscoverSchema } from "../../hooks/useDiscoverSchema";
import { useProfilingStatus } from "../../hooks/useProfilingStatus";
import { InlineErrorBanner } from "../InlineErrorBanner";

export interface DiscoveryDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly connId: string;
  /** Called when the user clicks "Vai allo schema" after a successful run. */
  readonly onOpenSchema?: () => void;
}

function isTerminal(status: ProfilingStatusResponse | undefined): boolean {
  if (!status) return false;
  return status.status === "complete" || status.status === "no_schemas";
}

export const DiscoveryDialog: React.FC<DiscoveryDialogProps> = ({
  open,
  onOpenChange,
  connId,
  onOpenSchema,
}) => {
  const { t } = useLingui();
  const discoverMutation = useDiscoverSchema();
  const statusQuery = useProfilingStatus(connId, { enabled: open });

  // Explicit confirmation gate. Reset on every close so the next open
  // asks again — this is a destructive, irreversible operation.
  const [confirmed, setConfirmed] = React.useState(false);
  React.useEffect(() => {
    if (!open) setConfirmed(false);
  }, [open]);

  // Kick off discovery exactly once per confirmed "open" cycle. The
  // mutation is idempotent server-side (re-issuing returns
  // ``{status:"started"}``) but firing it once keeps the UI deterministic.
  const launchedRef = React.useRef(false);
  React.useEffect(() => {
    if (!open || !confirmed) {
      if (!open) launchedRef.current = false;
      return;
    }
    if (launchedRef.current) return;
    launchedRef.current = true;
    discoverMutation.mutate({ connId });
  }, [open, confirmed, connId, discoverMutation]);

  const status = statusQuery.data;
  const profiled = status?.profiled_tables ?? 0;
  const total = status?.total_tables ?? 0;
  const progressPct = total > 0 ? Math.min(100, Math.round((profiled / total) * 100)) : 0;
  const terminal = isTerminal(status);
  const error =
    discoverMutation.error?.message ??
    status?.discovery_error ??
    (statusQuery.isError ? t`Impossibile leggere lo stato di scoperta.` : null);

  if (!confirmed) {
    const contextLocked = status?.business_context_user_edited === true;
    const hasExistingSchema = (status?.total_tables ?? 0) > 0;
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          ariaTitle={t`Rigenerare tutto lo schema?`}
          className="w-[min(600px,94vw)] max-w-none gap-4 bg-cream"
        >
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ando-orange/15">
              <AlertTriangle className="h-5 w-5 text-ando-orange" aria-hidden />
            </span>
            <div className="flex flex-col gap-1">
              <DialogTitle className="text-[17px] leading-tight">
                <Trans>Rigenerare tutto lo schema?</Trans>
              </DialogTitle>
              <DialogDescription className="text-[13px] text-ink-2">
                <Trans>
                  La scoperta rilegge la struttura dal database e rilancia la profilazione AI su
                  tutte le tabelle. Non si può annullare a metà e non si può tornare indietro.
                </Trans>
              </DialogDescription>
            </div>
          </div>

          <div
            role="alert"
            className="rounded-md border border-ando-orange/40 bg-ando-orange/8 px-4 py-3 text-[13px] leading-relaxed text-ink-1"
          >
            <p className="font-semibold">
              <Trans>Si perde tutto quello che l'AI aveva prodotto:</Trans>
            </p>
            <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-ink-2">
              <li><Trans>descrizioni AI di tabelle e colonne</Trans></li>
              <li><Trans>valori campione, mappature semantiche e relazioni rilevate</Trans></li>
              <li><Trans>embedding dello schema (ricalcolati da zero)</Trans></li>
              <li><Trans>esempi e affinamenti maturati con le query precedenti</Trans></li>
            </ul>
            {hasExistingSchema ? (
              <p className="mt-2 text-ink-2">
                <Plural
                  value={status?.total_tables ?? 0}
                  one="Interessa # tabella già profilata."
                  other="Interessa # tabelle già profilate."
                />
              </p>
            ) : null}
          </div>

          <div className="flex items-start gap-2 rounded-md border border-hairline bg-white/50 px-3 py-2 text-[12.5px] text-ink-2">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
            <span>
              <Trans>
                Restano intatte solo le descrizioni modificate a mano da una persona
              </Trans>
              {contextLocked ? (
                <>
                  {" "}
                  <Trans>e il contesto di business, che è protetto.</Trans>
                </>
              ) : (
                <>
                  {" "}
                  <Trans>
                    — il contesto di business attuale invece verrà riscritto dall'AI.
                  </Trans>
                </>
              )}
            </span>
          </div>

          <div className="mt-1 flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              <Trans>Annulla</Trans>
            </Button>
            <Button type="button" variant="primary" size="sm" onClick={() => setConfirmed(true)}>
              <Trans>Sì, rigenera tutto</Trans>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ariaTitle={t`Scoperta schema`}
        className="w-[min(520px,92vw)] max-w-none gap-3 bg-cream"
      >
        <DialogTitle className="text-[15px]">
          {terminal && status?.status === "complete" ? (
            <Trans>Schema pronto</Trans>
          ) : (
            <Trans>Scoperta schema in corso</Trans>
          )}
        </DialogTitle>
        <DialogDescription className="text-[12.5px]">
          <Trans>
            Analizziamo le tabelle disponibili e generiamo descrizioni AI per ciascuna. Puoi chiudere
            questa finestra: il lavoro continua in background.
          </Trans>
        </DialogDescription>

        {/* ── Progress bar ───────────────────────────────────────── */}
        <div className="mt-1 flex flex-col gap-1.5">
          <div className="flex items-center justify-between text-[11.5px] text-ink-2">
            <span>
              <Trans>{profiled} di {total} profilati</Trans>
            </span>
            <span className="font-medium text-ink-1 tabular-nums">{progressPct}%</span>
          </div>
          <div
            role="progressbar"
            tabIndex={0}
            aria-valuemin={0}
            aria-valuemax={total > 0 ? total : 100}
            aria-valuenow={profiled}
            aria-label={t`Avanzamento profilazione`}
            className="h-2 w-full overflow-hidden rounded-full bg-ink-3/15"
          >
            <div
              className={cn(
                "h-full rounded-full bg-ando-orange transition-[width] duration-500",
                terminal && status?.status === "complete" ? "bg-success" : undefined,
              )}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* ── Status detail row ──────────────────────────────────── */}
        <div className="mt-2 flex items-center gap-2 rounded-md border border-hairline bg-white/40 px-3 py-2 text-[12px]">
          {!terminal ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin text-ando-orange" aria-hidden />
              <span className="text-ink-2">
                {status?.is_discovering ? (
                  <Trans>Lettura dello schema dal connettore…</Trans>
                ) : status?.is_profiling ? (
                  <Trans>Analisi tabelle in corso…</Trans>
                ) : (
                  <Trans>Avvio del job di scoperta…</Trans>
                )}
              </span>
            </>
          ) : status?.status === "complete" ? (
            <>
              <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden />
              <span className="text-ink-1">
                <Plural
                  value={total}
                  one="Scoperta completata: # tabella pronta."
                  other="Scoperta completata: # tabelle pronte."
                />
              </span>
            </>
          ) : (
            <>
              <AlertCircle className="h-3.5 w-3.5 text-warning" aria-hidden />
              <span className="text-ink-1">
                <Trans>Nessuna tabella trovata. Verifica permessi e schema del connettore.</Trans>
              </span>
            </>
          )}
        </div>

        <InlineErrorBanner message={error ?? null} />

        {/* ── Table-by-table status (when total >= 1) ────────────── */}
        {total > 0 ? (
          <div className="mt-1 flex flex-col gap-1 text-[11.5px]">
            <div className="flex items-center gap-1.5 text-ink-3">
              <CircleDot className="h-3 w-3" aria-hidden />
              <span><Trans>{Math.max(0, total - profiled)} in attesa</Trans></span>
              <span aria-hidden>·</span>
              <CheckCircle2 className="h-3 w-3 text-success" aria-hidden />
              <span><Trans>{profiled} profilate</Trans></span>
            </div>
          </div>
        ) : null}

        {/* ── Footer actions ─────────────────────────────────────── */}
        <div className="mt-3 flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            <Trans>Chiudi</Trans>
          </Button>
          {terminal && status?.status === "complete" && onOpenSchema ? (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => {
                onOpenSchema();
                onOpenChange(false);
              }}
            >
              <Trans>Vai allo schema</Trans>
            </Button>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};
DiscoveryDialog.displayName = "DiscoveryDialog";
